import React, { FC, useEffect, useRef, useState } from 'react';
import { Box, Button, Flex, Text, Switch } from 'src/components/ui';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import GoogleMapsAutocomplete from '../common/form/GoogleMapsAutocomplete';
import GoogleMapsComponent from '../common/form/GoogleMapsComponent';
import { StringInputHook, PhoneInput } from '../common/form';
import CustomModal from 'src/components/common/CustomModal';
import { validatePhoneNumber } from 'src/lib/utils/phoneValidation';

const LocationFormSchema = z.object({
	name: z.string().min(1, 'Name is required'),
	phone: z.string().optional(),
	isDefault: z.boolean().default(false),
});

type LocationFormData = z.infer<typeof LocationFormSchema>;

type AddressWithFormatted = {
	street: string;
	city: string;
	state: string;
	postalCode: string;
	country: string;
	lat: string | number;
	long: string | number;
	formatted?: string;
	placeId?: string;
	regionCode?: string;
};

interface LocationDraft {
	name: string;
	address: AddressWithFormatted;
	isDefault: boolean;
	phone?: string;
}

interface LocationModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSave: (locationData: LocationDraft) => Promise<void>;
	locationDraft: LocationDraft | null;
	isSavingLocation: boolean;
	editingLocationIdx: number | null;
}

const LocationModal: FC<LocationModalProps> = ({
	isOpen,
	onClose,
	onSave,
	locationDraft,
	isSavingLocation,
	editingLocationIdx,
}) => {
	const formMethods = useForm<LocationFormData>({
		resolver: zodResolver(LocationFormSchema),
		defaultValues: {
			name: '',
			phone: '',
			isDefault: false,
		},
	});

	const { handleSubmit, reset, setValue, watch } = formMethods;

	// Local state for address (since it's not part of the main form schema)
	const [address, setAddress] = useState<AddressWithFormatted>({
		street: '',
		city: '',
		state: '',
		postalCode: '',
		country: '',
		lat: '',
		long: '',
		formatted: '',
		placeId: '',
		regionCode: '',
	});

	const [addressError, setAddressError] = useState<string>('');
	const [phoneError, setPhoneError] = useState<string>('');

	// Map interaction state to prevent programmatic updates after user interaction
	const [mapKey, setMapKey] = useState<string>('initial');

	// Reset form when modal opens/closes or when locationDraft changes
	useEffect(() => {
		if (isOpen && locationDraft) {
			reset({
				name: locationDraft.name,
				phone: locationDraft.phone,
				isDefault: locationDraft.isDefault,
			});
			setAddress(locationDraft.address);
			setAddressError('');
			setPhoneError('');

			// Reset map interaction state and force map refresh
			setMapKey(`edit-${Date.now()}`);
		} else if (isOpen && !locationDraft) {
			// New location
			reset({
				name: '',
				phone: '',
				isDefault: false,
			});
			setAddress({
				street: '',
				city: '',
				state: '',
				postalCode: '',
				country: '',
				lat: '',
				long: '',
				formatted: '',
				placeId: '',
				regionCode: '',
			});
			setAddressError('');
			setPhoneError('');

			// Reset map interaction state
			setMapKey(`new-${Date.now()}`);
		}
	}, [isOpen, locationDraft, reset]);

	// Track last geocoded address to prevent duplicate requests
	const lastGeocodedAddressRef = useRef<string>('');

	// Geocode prefilled address if no coordinates are available
	useEffect(() => {
		const geocodeAddress = async () => {
			// Only geocode if we have a formatted address but no coordinates
			// and we haven't already tried to geocode this address
			if (
				isOpen &&
				address.formatted &&
				address.formatted !== lastGeocodedAddressRef.current &&
				(!address.lat || address.lat === '' || address.lat === '0') &&
				(!address.long || address.long === '' || address.long === '0') &&
				window.google?.maps?.Geocoder
			) {
				// Mark this address as attempted
				lastGeocodedAddressRef.current = address.formatted;

				try {
					const geocoder = new window.google.maps.Geocoder();
					const result = await geocoder.geocode({ address: address.formatted });

					if (result.results && result.results.length > 0) {
						const place = result.results[0];
						const location = place.geometry?.location;

						if (location) {
							// Extract address components
							const components = place.address_components || [];
							let streetNumber = '';
							let route = '';
							let premise = '';
							let establishment = '';
							let pointOfInterest = '';
							let city = '';
							let state = '';
							let postalCode = '';
							let country = '';

							for (const component of components) {
								const types = component.types;
								if (types.includes('street_number')) {
									streetNumber = component.long_name;
								} else if (types.includes('route')) {
									route = component.long_name;
								} else if (types.includes('premise')) {
									premise = component.long_name;
								} else if (types.includes('establishment')) {
									establishment = component.long_name;
								} else if (types.includes('point_of_interest') || types.includes('park')) {
									pointOfInterest = component.long_name;
								} else if (types.includes('locality') || types.includes('sublocality')) {
									city = component.long_name;
								} else if (types.includes('administrative_area_level_1')) {
									state = component.short_name;
								} else if (types.includes('postal_code')) {
									postalCode = component.long_name;
								} else if (types.includes('country')) {
									country = component.long_name;
								}
							}

							let street = '';
							if (streetNumber && route) {
								street = `${streetNumber} ${route}`;
							} else if (streetNumber || route) {
								street = streetNumber || route;
							} else if (premise) {
								street = premise;
							} else if (establishment) {
								street = establishment;
							} else if (pointOfInterest) {
								street = pointOfInterest;
							}

							if (!street.trim() && place.formatted_address) {
								const parts = place.formatted_address.split(',').map((p: string) => p.trim());
								if (parts.length > 0 && parts[0] !== city) {
									street = parts[0];
								}
							}

							setAddress((prev) => ({
								...prev,
								lat: location.lat().toString(),
								long: location.lng().toString(),
								street: street.trim() || prev.street,
								city: city || prev.city,
								state: state || prev.state,
								postalCode: postalCode || prev.postalCode,
								country: country || prev.country,
								placeId: place.place_id || prev.placeId,
							}));

							// Update map
							setMapKey(`geocoded-${Date.now()}`);
						}
					}
				} catch (error) {
					console.error('Geocoding failed:', error);
				}
			}
		};

		geocodeAddress();
	}, [isOpen, address.formatted, address.lat, address.long]);

	// Reset geocoded address tracking when modal closes
	useEffect(() => {
		if (!isOpen) {
			lastGeocodedAddressRef.current = '';
		}
	}, [isOpen]);

	const onSubmit = handleSubmit(async (formData) => {
		// Validate address
		if (!address.street?.trim()) {
			setAddressError('Street is required');
			return;
		}
		if (!address.city?.trim()) {
			setAddressError('City is required');
			return;
		}
		// State is optional for international addresses
		// if (!address.state?.trim()) {
		// 	setAddressError('State is required');
		// 	return;
		// }
		if (!address.country?.trim()) {
			setAddressError('Country is required');
			return;
		}
		if (!address.postalCode?.trim()) {
			setAddressError('Postal Code is required');
			return;
		}

		setAddressError('');
		setPhoneError('');

		// Validate phone number only if provided
		let validatedPhone = formData.phone;
		if (formData.phone && formData.phone.trim()) {
			const phoneValidation = await validatePhoneNumber(
				formData.phone,
				address.country,
				{
					city: address.city,
					state: address.state,
					postalCode: address.postalCode,
				},
			);

			if (!phoneValidation.success) {
				setPhoneError(phoneValidation.message);
				return;
			}

			validatedPhone = phoneValidation.formattedPhoneNumber || formData.phone;
		}

		const locationData: LocationDraft = {
			...formData,
			phone: validatedPhone,
			address,
		};

		await onSave(locationData);
	});

	const handleAddressChange = (
		newAddress: AddressWithFormatted,
		formatted: string,
	) => {
		setAddress({ ...newAddress, formatted });
		if (addressError) setAddressError('');
		if (phoneError) setPhoneError('');

		// Update map center and zoom only when selecting new address from autocomplete
		if (
			newAddress.lat &&
			newAddress.long &&
			!isNaN(Number(newAddress.lat)) &&
			!isNaN(Number(newAddress.long))
		) {
			// Force map refresh with new address
			setMapKey(`address-${Date.now()}`);
		}
	};

	const handleAddressInputChange = (value: string) => {
		setAddress((prev) => ({ ...prev, formatted: value }));
		if (addressError) setAddressError('');
		if (phoneError) setPhoneError('');
	};

	const handleMapMarkerDrag = (lat: number, lng: number) => {
		setAddress((prev) => ({
			...prev,
			lat: lat.toString(),
			long: lng.toString(),
		}));
	};

	const handlePlaceSelect = (placeId: string, addressDetails: any) => {
		setAddress((prev) => ({
			...prev,
			lat: addressDetails.lat,
			long: addressDetails.long,
			street: addressDetails.street,
			city: addressDetails.city,
			state: addressDetails.state,
			postalCode: addressDetails.postalCode,
			country: addressDetails.country,
			formatted: addressDetails.formatted,
			placeId: placeId,
			regionCode: addressDetails.regionCode,
		}));
	};

	const isDefaultChecked = watch('isDefault');

	if (!isOpen) return null;

	return (
		<CustomModal
			isOpen={isOpen}
			onClose={onClose}
			header={editingLocationIdx === null ? 'Add Location' : 'Edit Location'}
			footer={
				<Flex
					w="full"
					direction={{ base: 'column-reverse', md: 'row' }}
					justifyContent={{ base: 'stretch', md: 'flex-end' }}
					gap={{ base: 3, md: 2 }}
				>
					<Button
						onClick={onClose}
						variant="ghost"
						w={{ base: 'full', md: 'auto' }}
					>
						Cancel
					</Button>
					<Button
						onClick={(e) => {
							onSubmit(e);
						}}
						variant="orangeSolid"
						isLoading={isSavingLocation}
						w={{ base: 'full', md: 'auto' }}
					>
						Save
					</Button>
				</Flex>
			}
			size="xl"
			stickyFooter
			maxWidth={{ base: '95vw', md: 'xl' }}
			minWidth={{ base: '95vw', md: 'xl' }}
		>
			<FormProvider {...formMethods}>
				<form onSubmit={onSubmit}>
					<Flex direction="column" gap={3} p={2} overflowY="auto">
						<StringInputHook
							name="name"
							label="Location Name"
							placeholder="e.g. Headquarters"
							required
						/>
						<PhoneInput
							name="phone"
							label="Location Phone"
							placeholder="e.g. 555-123-4567"
							error={phoneError}
							disableAutoFormat={true}
						/>

						<GoogleMapsAutocomplete
							value={address.formatted || ''}
							onChange={handleAddressChange}
							onInputChange={handleAddressInputChange}
							error={addressError}
							label="Location Address"
							placeholder="Enter address or place name"
							required
							autoSearch={!!address.formatted && !address.street}
						/>
						<Box mt={3} mb={2} minH="200px" maxW="100%">
							<GoogleMapsComponent
								mapKey={mapKey}
								markerPosition={
									address.lat &&
									address.long &&
									!isNaN(Number(address.lat)) &&
									!isNaN(Number(address.long)) &&
									address.lat.toString().trim() !== '' &&
									address.long.toString().trim() !== ''
										? { lat: Number(address.lat), lng: Number(address.long) }
										: undefined
								}
								onMarkerDrag={handleMapMarkerDrag}
								onPlaceSelect={handlePlaceSelect}
							/>
						</Box>
						<Flex alignItems="center" justifyContent="flex-end" gap={2}>
							<Text fontSize="sm">Headquarters</Text>
							<Switch
								isChecked={isDefaultChecked}
								onChange={(e) => {
									setValue('isDefault', e.target.checked);
								}}
								colorScheme="blue"
								size="md"
							/>
						</Flex>
					</Flex>
				</form>
			</FormProvider>
		</CustomModal>
	);
};

export default LocationModal;
